






import { Component, Inject, Prop, Vue, Watch } from "vue-property-decorator";

import Keycloak from "@apphq/keycloak-js";
import AuthModule from "../store";

@Component
export default class KeycloakIFrame extends Vue {
  @Inject() authStore!: AuthModule;

  @Prop({ type: String, required: true }) redirectUri!: string;
  @Prop({ type: String, required: false }) locale?: string;

  keycloak!: Keycloak.KeycloakInstance;

  mounted() {
    window.addEventListener("message", this.onMessage.bind(this), false);

    this.loadKeycloak();
  }

  get isLoggedIn() {
    return this.authStore.isLoggedIn;
  }
  get isLoggedOut() {
    return this.authStore.isLoggedOut;
  }

  @Watch("isLoggedIn")
  onRequestLogout(loggedIn: boolean) {
    if (loggedIn === false) {
      this.logout();
    }
  }

  @Watch("locale")
  onLocaleChange(locale: string | undefined) {
    if (this.isLoggedIn === false) {
      this.keycloak.login({ kcLocale: locale });
    }
  }

  loadKeycloak() {
    const keycloakConfig = {
      url: process.env.VUE_APP_RAS_KEYCLOAK_SERVER,
      realm: process.env.VUE_APP_RAS_KEYCLOAK_REALM,
      clientId: process.env.VUE_APP_RAS_KEYCLOAK_CLIENT_ID
    };

    // @ts-ignore
    this.keycloak = Keycloak(keycloakConfig, this.$refs.iframe.contentWindow);
    this.keycloak.redirectUri = this.redirectUri;

    this.keycloak.onAuthSuccess = this.onKeycloakSuccess.bind(this);
    this.keycloak.onAuthError = this.onKeycloakFailure.bind(this);
    this.keycloak.onAuthRefreshSuccess = this.onTokenRefreshSuccess.bind(this);
    this.keycloak.onAuthRefreshError = this.onTokenRefreshFailure.bind(this);
    this.keycloak.onAuthLogout = this.onKeycloakLogout.bind(this);
    this.keycloak.onTokenExpired = this.checkToken.bind(this);

    // @ts-ignore
    this.keycloak
      .init()
      .success(() => this.keycloak.login({ kcLocale: this.locale }));
  }

  getOrigin() {
    return (
      window.location.origin ||
      window.location.protocol + "://" + window.location.host
    );
  }

  logout() {
    this.keycloak.logout();
  }

  onMessage(event: MessageEvent) {
    if (event.origin === this.getOrigin() && event.data === "beacon-load") {
      /**
       * After login and logout the iframe is redirected to the beacon page.
       * For the login we need to recreate the keycloak, because it does not
       * automatically detect, that the login was finished.
       * In the case of an explicit or implicit logout, keycloak learns about
       * it through its own login state iframe.
       */
      if (!this.keycloak.authenticated) {
        this.loadKeycloak();
      }
    }
  }

  onIframeLoad() {
    if (!this.isLoggedOut && !this.isLoggedIn) {
      this.onKeycloakLogout();
    }
  }

  async onKeycloakSuccess() {
    if (this.keycloak.authenticated) {
      this.watchToken();

      try {
        this.authStore.updateRoles(
          (this.keycloak.realmAccess || { roles: [] }).roles
        );
        if (this.keycloak.token) {
          this.authStore.login(this.keycloak.token);
          this.$emit("login");
        } else {
          this.onKeycloakFailure();
        }
      } catch (_) {
        this.onKeycloakFailure();
      }
    }
  }

  onKeycloakFailure() {
    this.onKeycloakLogout();
  }

  onKeycloakLogout() {
    this.authStore.reset();
    this.$emit("logout");
  }

  /**
   * Automatically refresh token before it expires
   */
  watchToken() {
    let tokenTimeout: number;
    if (
      this.keycloak.tokenParsed &&
      this.keycloak.tokenParsed.exp != null &&
      this.keycloak.timeSkew != null
    ) {
      tokenTimeout =
        1000 * (this.keycloak.tokenParsed.exp + this.keycloak.timeSkew) -
        Date.now();
    } else {
      tokenTimeout = 1000 * 60;
    }
    window.setTimeout(
      this.checkToken.bind(this),
      Math.max(1000, Math.min(tokenTimeout - 30 * 1000, tokenTimeout / 2))
    );
  }

  checkToken() {
    this.keycloak.updateToken(60);
  }

  onTokenRefreshSuccess() {
    this.watchToken();
    this.authStore.updateToken(this.keycloak.token || "");
  }

  onTokenRefreshFailure() {
    this.logout();
  }
}
